<template>
  <div v-loading="isFetchingData" :key="resetKey" class="analytics">
    <AnalyticsHeader @onChangeSubTab="$store.state.Analytics.activeSubTab = $event" />
    <div class="analytics__container">
      <AnalyticsOverview v-show="$store.state.Analytics.activeSubTab === AnalyticsType.OVERVIEW" />
      <AnalyticsAdvanced v-show="$store.state.Analytics.activeSubTab === AnalyticsType.ADVANCED" />
    </div>
  </div>
</template>

<script>
import AnalyticsHeader from '@/views/Analytics/AnalyticsHeader';
import AnalyticsAdvanced from '@/views/Analytics/AnalyticsAdvanced/AnalyticsAdvanced';
import AnalyticsOverview from '@/views/Analytics/AnalyticsOverview/AnalyticsOverview';
import { AnalyticsType } from '@/utils/common';
import { debounce } from '@/utils/helpers';
import { isRemovedPlatform } from '@/utils/services';

export default {
  name: 'Analytics',
  components: {
    AnalyticsHeader,
    AnalyticsAdvanced,
    AnalyticsOverview,
  },
  data: () => ({
    AnalyticsType,
    resetKey: 0,
  }),
  computed: {
    isFetchingData() {
      return this.$store.state.Analytics[this.$store.state.Analytics.activeSubTab].isLoading;
    },
    activeAccount() {
      return this.$store.state.Auth.activeAccount;
    },
    overviewAnalyticsData() {
      return this.$store.state.Analytics[AnalyticsType.OVERVIEW].data;
    },
    advancedAnalyticsData() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].data;
    },
    visibleServices() {
      const services = this.$store.state.Auth.activeAccount.visible_services;
      return Object.keys(services).filter((key) => services[key] && !isRemovedPlatform(key));
    },
    advancedAnalyticsServices() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].selectedServices;
    },
  },
  watch: {
    '$store.state.Analytics.dateRange': {
      handler: debounce(async function (newVal, oldVal) {
        if (!this.activeAccount) return;

        this.$store.commit('Analytics/toggleLoading', {
          value: true,
          type: AnalyticsType.OVERVIEW,
        });
        this.$store.commit('Analytics/toggleLoading', {
          value: true,
          type: AnalyticsType.ADVANCED,
        });

        if (newVal.date_from !== oldVal?.date_from || newVal.date_to !== oldVal?.date_to) {
          await this.fetchOverviewAnalyticsData();

          if (this.advancedAnalyticsServices.length) {
            await this.fetchAdvancedAnalyticsData();
          }
        }

        this.$store.commit('Analytics/toggleLoading', {
          value: false,
          type: AnalyticsType.OVERVIEW,
        });
        this.$store.commit('Analytics/toggleLoading', {
          value: false,
          type: AnalyticsType.ADVANCED,
        });
      }, 1000),
      deep: true,
      immediate: true,
    },
    activeAccount: {
      async handler(newVal, oldVal) {
        this.$store.commit('Analytics/toggleLoading', {
          value: true,
          type: AnalyticsType.OVERVIEW,
        });
        this.$store.commit('Analytics/toggleLoading', {
          value: true,
          type: AnalyticsType.ADVANCED,
        });

        if (!newVal || newVal.id === oldVal?.id) return;
        this.$store.commit('Analytics/resetState');
        this.resetKey += 1; // Принудительно пересоздаём компонент
        this.$store.state.Analytics.activeSubTab = AnalyticsType.OVERVIEW;

        await this.fetchOverviewAnalyticsData();

        if (this.advancedAnalyticsServices.length) {
          await this.fetchAdvancedAnalyticsData();
        }

        this.$store.commit('Analytics/toggleLoading', {
          value: false,
          type: AnalyticsType.OVERVIEW,
        });
        this.$store.commit('Analytics/toggleLoading', {
          value: false,
          type: AnalyticsType.ADVANCED,
        });
      },
      immediate: true,
    },
  },
  methods: {
    async fetchOverviewAnalyticsData() {
      try {
        await this.$store.dispatch('Analytics/getOverviewAnalytics', this.activeAccount.id);
      } catch (e) {
        console.error('Failed to fetch analytics:', e);
      }
    },
    async fetchAdvancedAnalyticsData() {
      try {
        await this.$store.dispatch('Analytics/getAdvancedAnalytics', this.activeAccount.id);
      } catch (e) {
        console.error('Failed to fetch analytics:', e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/helpers/variables';
.analytics {
  &__placeholder {
    margin-top: 74px;
  }

  &__container {
    margin-top: 20px;
  }
}
</style>
